import { useEffect, useState, useRef, useCallback } from "react"
import HCRender from "./hc-render"
import useContentLoader from "./hooks/useContentLoader"
import useNavigation from "./hooks/useNavigation"
import useBreadcrumbs from "./hooks/useBreadcrumbs"
import useSearch from "./hooks/useSearch"
import { getPathFromUrl } from "./urlUtils"
import { expandCategoryPath } from "./categoryUtils"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"

/**
 * Main Help Center controller component.
 * All visual rendering is delegated to overridable fragments via HCRender.
 */
async function onBeforeRender(pageContext) {
  const { getCanonicalUrl } = await import('aldoo-ra/AldooVike/app')
  const canonicalUrl = getCanonicalUrl(pageContext)
  if (!canonicalUrl) return {}
  return { metadata: { canonicalUrl } }
}

const HCHome = () => {
  const texts = TextBundle({ id: "help-center" })

  const [refreshKey, setRefreshKey] = useState(0)
  const contentLoader = useContentLoader()

  const {
    selectedContent,
    contentType,
    isLoading,
    error,
    expandedCategories,
    loadArticle,
    loadCategory,
    setExpandedCategories,
    updateUrl,
    refreshContent,
    tocData,
    currentLanguage,
  } = contentLoader

  const [prevLanguage, setPrevLanguage] = useState(currentLanguage)
  const hasResetCategoriesRef = useRef(false)

  const currentPath = getPathFromUrl()

  const [currentUrl, setCurrentUrl] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : ""
  )

  useEffect(() => {
    if (typeof window === "undefined") return
    const updateCurrentUrl = () => setCurrentUrl(window.location.pathname)
    updateCurrentUrl()
    window.addEventListener("popstate", updateCurrentUrl)
    window.addEventListener("helpCenterNavigate", updateCurrentUrl)
    return () => {
      window.removeEventListener("popstate", updateCurrentUrl)
      window.removeEventListener("helpCenterNavigate", updateCurrentUrl)
    }
  }, [])

  useEffect(() => {
    if (typeof window !== "undefined" && currentUrl !== window.location.pathname) {
      setCurrentUrl(window.location.pathname)
    }
  }, [currentUrl])

  const isSubmitRequestPage =
    currentUrl.includes("/help-center/submit-request") ||
    (typeof window !== "undefined" &&
      window.location.pathname.includes("/help-center/submit-request"))

  const isHomePage = !currentPath?.slug && !selectedContent && !isSubmitRequestPage

  useEffect(() => {
    if (prevLanguage !== currentLanguage) {
      setPrevLanguage(currentLanguage)
      setExpandedCategories({})
      refreshContent()
      setRefreshKey((prev) => prev + 1)
    }
  }, [currentLanguage, prevLanguage, refreshContent, setExpandedCategories])

  useEffect(() => {
    if (isSubmitRequestPage) return

    const isOnHomePage =
      currentPath &&
      !currentPath.slug &&
      (!currentPath.type || currentPath.type === null) &&
      (!currentPath.categoryPath || currentPath.categoryPath === null)

    if (isOnHomePage) {
      if (!hasResetCategoriesRef.current) {
        if (expandedCategories && Object.keys(expandedCategories).length > 0) {
          setExpandedCategories({})
        }
        if (!/\/help-center\/?$/.test(currentUrl)) {
          const hcBase = currentUrl.substring(0, currentUrl.indexOf("/help-center") + "/help-center".length)
          window.history.replaceState(null, "", hcBase)
          setCurrentUrl(hcBase)
        }
        hasResetCategoriesRef.current = true
      }
    } else {
      hasResetCategoriesRef.current = false
    }
  }, [currentPath, expandedCategories, setExpandedCategories, isSubmitRequestPage, currentUrl])

  const safeExpandCategoryPath = useCallback(
    (path) => {
      if (!path) { setExpandedCategories({}); return }

      const cleanPath =
        typeof path === "string"
          ? path.split("/").filter(Boolean).join("/")
          : Array.isArray(path)
          ? path.filter(Boolean)
          : null

      if (!cleanPath) { setExpandedCategories({}); return }

      expandCategoryPath(cleanPath, setExpandedCategories)
    },
    [setExpandedCategories]
  )

  const handleArticleSelect = useCallback(
    (slug, categoryPath) => {
      if (!slug) return
      updateUrl("article", slug, categoryPath)
      if (categoryPath) safeExpandCategoryPath(categoryPath)
      else setExpandedCategories({})
      loadArticle(slug, categoryPath)
    },
    [updateUrl, setExpandedCategories, safeExpandCategoryPath, loadArticle]
  )

  const handleCategorySelect = useCallback(
    (slug, parentPath) => {
      if (!slug || typeof slug !== "string") return

      const normalizedParentPath =
        parentPath && typeof parentPath === "string" ? parentPath : ""
      const fullPath = normalizedParentPath ? `${normalizedParentPath}/${slug}` : slug

      const newExpandedCategories = { ...expandedCategories }
      const wasExpanded = newExpandedCategories[fullPath]
      newExpandedCategories[fullPath] = !wasExpanded
      setExpandedCategories(newExpandedCategories)

      if (!wasExpanded) {
        updateUrl("category", slug, normalizedParentPath)
        loadCategory(fullPath)
      }
    },
    [expandedCategories, setExpandedCategories, updateUrl, loadCategory]
  )

  useEffect(() => {
    if (typeof window === "undefined") return
    window.loadHelpCenterContent = (type, slug, categoryPath) => {
      if (type === "article") handleArticleSelect(slug, categoryPath || null)
      else if (type === "category") handleCategorySelect(slug, categoryPath || "")
    }
    return () => { delete window.loadHelpCenterContent }
  }, [handleArticleSelect, handleCategorySelect])

  const handleSearchResultSelect = useCallback(
    (result) => {
      if (!result || !result.type) return

      setExpandedCategories({})

      if (result.type === "article") {
        if (!result.item || !result.item.slug) return

        let categoryPath = null
        if (result.slugPath && result.slugPath.length > 0) {
          categoryPath = result.slugPath.filter(Boolean).join("/") || null
        } else if (result.category?.slug) {
          categoryPath = result.category.slug
        }

        handleArticleSelect(result.item.slug, categoryPath)
      } else if (result.type === "category") {
        if (!result.item || !result.item.slug) return

        handleCategorySelect(result.item.slug, result.category?.slug || "")
      }
    },
    [setExpandedCategories, handleArticleSelect, handleCategorySelect]
  )

  useEffect(() => {
    // For articles, loadArticle derives the full tree path from TOC breadcrumbs and handles expansion.
    // Only run URL-based expansion for categories where the path is already correct.
    if (currentPath.categoryPath && tocData && currentPath.type !== "article") {
      safeExpandCategoryPath(currentPath.categoryPath)
    }
  }, [
    currentPath.categoryPath,
    currentPath.type,
    currentPath.directParentPath,
    tocData,
    safeExpandCategoryPath,
  ])

  const [isClient, setIsClient] = useState(false)
  useEffect(() => { setIsClient(true) }, [])

  const { navigateTo } = useNavigation(contentLoader)
  const { breadcrumbs = [] } = useBreadcrumbs(contentLoader, navigateTo) || {}
  const searchState = useSearch()

  const shouldShowLoader = isLoading && !tocData && isClient && !isHomePage

  const PulsingLoader = HCRender.getFragment("PulsingLoader")
  const HomeLayout = HCRender.getFragment("HomeLayout")
  const SubmitRequest = HCRender.getFragment("SubmitRequest")

  if (shouldShowLoader) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          {PulsingLoader && <PulsingLoader />}
          <Typography className="text-gray_700 mt-4">
            {texts?.loading_hc || "Loading Help Center..."}
          </Typography>
        </div>
      </div>
    )
  }

  if (error && !tocData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <Typography variant="error">{error}</Typography>
          <button
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={refreshContent}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (isSubmitRequestPage) {
    return SubmitRequest ? <SubmitRequest /> : null
  }

  if (!HomeLayout) return null

  return (
    <HomeLayout
      isHomePage={isHomePage}
      breadcrumbs={breadcrumbs}
      refreshKey={refreshKey}
      currentLanguage={currentLanguage}
      searchState={searchState}
      onSearchResultSelect={handleSearchResultSelect}
      rootCategoriesData={tocData}
      onArticleSelect={handleArticleSelect}
      onCategorySelect={handleCategorySelect}
      expandedCategories={expandedCategories}
      currentPath={currentPath}
      isLoading={isLoading}
      error={error}
      selectedContent={selectedContent}
      contentType={contentType}
      navigateTo={navigateTo}
    />
  )
}

HCHome.onBeforeRender = onBeforeRender

export default HCHome
